/**
 * A custom element that upgrades blog article body content into museum exhibits:
 * - Serves crisp responsive images (srcset built from the Shopify CDN base URL)
 * - Wraps standalone images in <figure> with placard-style captions. A caption is
 *   the paragraph immediately after an image that is entirely italic and/or
 *   wrapped in parentheses (the authoring convention used in article bodies).
 * - Supports optional per-image layout classes added in the editor's HTML view:
 *   `exhibit-left` / `exhibit-right` float the figure beside the text on desktop.
 */
const RESPONSIVE_WIDTHS = [352, 750, 1100, 1500, 2200];

class MuseumExhibit extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('img').forEach((img) => {
      const figure = this.#wrapInFigure(img);
      this.#makeResponsive(img, figure);
    });
  }

  /**
   * Wraps a standalone image (a paragraph containing only the image) in a
   * <figure>, promoting a caption paragraph to <figcaption> when present.
   * @param {HTMLImageElement} img
   * @returns {HTMLElement} the element the image ends up in
   */
  #wrapInFigure(img) {
    const container = img.parentElement;
    const standalone =
      container &&
      container !== this &&
      container.childElementCount === 1 &&
      container.textContent.trim() === '';

    if (!standalone) return img;

    const figure = document.createElement('figure');
    figure.classList.add('museum-figure');
    if (img.classList.contains('exhibit-left')) figure.classList.add('museum-figure--left');
    if (img.classList.contains('exhibit-right')) figure.classList.add('museum-figure--right');

    const captionSource = this.#captionFor(container);
    container.replaceWith(figure);
    figure.appendChild(img);

    if (captionSource) {
      const figcaption = document.createElement('figcaption');
      figcaption.classList.add('museum-caption');
      figcaption.textContent = captionSource.textContent.trim().replace(/^\((.*)\)$/s, '$1');
      figure.appendChild(figcaption);
      captionSource.remove();
    }

    return figure;
  }

  /**
   * Detects the caption paragraph for an image container: the next paragraph,
   * entirely italic and/or wrapped in parentheses.
   * @param {HTMLElement} container
   * @returns {HTMLElement | null}
   */
  #captionFor(container) {
    const next = container.nextElementSibling;
    if (!next || next.tagName !== 'P') return null;

    const text = next.textContent.trim();
    if (text === '') return null;
    if (text.startsWith('(') && text.endsWith(')')) return next;

    const clone = next.cloneNode(true);
    clone.querySelectorAll('em, i').forEach((node) => node.remove());
    const residue = clone.textContent.replace(/[()\s]/g, '');
    if (next.querySelector('em, i') && residue === '') return next;

    return null;
  }

  /**
   * Rebuilds the image's srcset from the CDN base URL so the browser can pick a
   * crisp size, instead of the single (often small, soft) size the editor saved.
   * @param {HTMLImageElement} img
   * @param {HTMLElement} figure
   */
  #makeResponsive(img, figure) {
    const src = img.getAttribute('src');
    if (!src) return;

    let url;
    try {
      url = new URL(src, window.location.origin);
    } catch {
      return;
    }
    const isShopifyCdn = url.hostname === 'cdn.shopify.com' || url.pathname.includes('/cdn/shop/');
    if (!isShopifyCdn) return;

    url.searchParams.delete('width');
    url.searchParams.delete('height');
    url.pathname = url.pathname.replace(/_(?:\d+x\d*|x\d+)(?=\.[a-z]+$)/i, '');
    const base = url.toString();
    const separator = base.includes('?') ? '&' : '?';

    img.srcset = RESPONSIVE_WIDTHS.map((width) => `${base}${separator}width=${width} ${width}w`).join(', ');
    if (!img.hasAttribute('loading')) img.loading = 'lazy';
    img.decoding = 'async';

    requestAnimationFrame(() => {
      const rendered = img.getBoundingClientRect().width || figure.getBoundingClientRect().width;
      img.sizes = rendered ? `${Math.ceil(rendered)}px` : '100vw';
    });
  }
}

if (!customElements.get('museum-exhibit')) {
  customElements.define('museum-exhibit', MuseumExhibit);
}
